import ButtonText from "@visa/vds/button-text";
import Col from "@visa/vds/col";
import Grid from "@visa/vds/grid";
import Row from "@visa/vds/row";
import Td from "@visa/vds/td";
import Tr from "@visa/vds/tr";
import Typography from "@visa/vds/typography";
import React, { useEffect, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { useHistory, useLocation } from "react-router-dom";
import PostClassification from "../../../components/library/PostClassification";
import PostTypeIcon from "../../../components/library/PostTypeIcon";
import MCSConfirmDialog from "../../../components/MCSConfirmDialog";
import MCSLoading from "../../../components/MCSLoading";
import SortableTable from "../../../components/MCSSortableTable";
import { AuthContext } from "../../../context/AuthContext";
import { GlobalAlertContext } from "../../../context/GlobalAlertContext";
import { LibraryAdminApi } from "../../../services/library.admin.service";
import { AdminRoutes } from "../../../_constants/PageRoutes";
import { LibraryHelper } from "../../../_helpers/LibraryHelper";
import ActionButtonGroup from "./Components/ActionButtonGroup";
import ActionMenu from "./Components/ActionMenu";
import ReorderNote from "./Components/ReorderNote";

function ManageCategory() {
  const location = useLocation();
  const history = useHistory();
  const [error, setError] = useState(null);
  const [category, setCategory] = useState(null);
  const [posts, setPosts] = useState(null);
  const [isReorder, setIsReorder] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const { promiseInProgress } = usePromiseTracker();
  const colHeaders = [
    {
      displayName: "Object Classification",
      name: "classification.name",
      orderable: false,
    },
    { displayName: "Type", name: "typeName", orderable: false },
    { displayName: "Display Title", name: "title", orderable: false },
    { displayName: "Admin Actions", name: "action", orderable: false },
  ];

  const handleClickEdit = (e) => {
    let id = Number(e.currentTarget.dataset.id);
    history.push({
      pathname: AdminRoutes.MANAGELIBRARY_POST,
      state: {
        postId: id,
        categoryId: category.id,
      },
    });
  };

  const handleClickAdd = () => {
    history.push({
      pathname: AdminRoutes.MANAGELIBRARY_POST,
      state: {
        postId: 0,
        categoryId: category.id,
      },
    });
  };

  const deleteConfirmHandler = (id, alertObj) => {
    trackPromise(
      LibraryAdminApi.deletePost(id)
        .then((res) => {
          alertObj.setSuccessMessage("Delete success!");
          fetchData();
        })
        .catch((error) =>
          alertObj.setErrorMessage("Unexpected error please try again.")
        )
    );
  };

  const handleClickDelete = (e) => {
    let id = e.currentTarget.dataset.id;
    let name = e.currentTarget.dataset.name;
    setConfirmDialog({
      ...confirmDialog,
      title: "Delete Object",
      type: "warning",
      message: "Are you sure you want to delete <b>" + name + "</b> ?",
      open: true,
      id: "delete-object",
      data: id,
    });
  };

  const fetchData = async () =>
    LibraryAdminApi.getCategory(location.state.categoryId)
      .then((res) => {
        setCategory(res.data);
        setPosts(res.data.libraryPosts);
      })
      .catch((error) => setError(error));

  const handleClickSaveReorder = (alertObj) => {
    let reOrdered = posts.map(function (d, index, arr) {
      d.orderNumber = index;
      return d;
    });
    trackPromise(
      LibraryAdminApi.updatePostOrder(reOrdered)
        .then((res) => {
          setCategory({ ...category, libraryPosts: reOrdered });
          alertObj.setSuccessMessage("Save success!");
        })
        .catch((error) => alertObj.setErrorMessage(error))
    );
  };

  const handleClickCancelReorder = () => {
    setPosts(category.libraryPosts);
  };

  useEffect(() => {
    if (location.state == null || location.state.categoryId == null) {
      history.push(AdminRoutes.MANAGELIBRARY);
    } else {
      trackPromise(fetchData());
    }
  }, []);

  if (error) {
    return <div>Error: {error.message}</div>;
  } else {
    return (
      <AuthContext.Consumer>
        {(data) => (
          <GlobalAlertContext.Consumer>
            {(alertObj) => (
              <>
                {confirmDialog && (
                  <MCSConfirmDialog
                    alertObj={alertObj}
                    confirmHandler={deleteConfirmHandler}
                    confirmDialog={confirmDialog}
                    setConfirmDialog={setConfirmDialog}
                  />
                )}
                {promiseInProgress && <MCSLoading />}
                {!promiseInProgress && category && posts && (
                  <Grid>
                    <Row>
                      <Col>
                        <Typography tag="h1">
                          {"Manage Category Objects - " + category.name}
                        </Typography>
                      </Col>
                    </Row>
                    <Row>
                      {!isReorder && (
                        <Col>
                          <ButtonText
                            colorScheme="tertiary"
                            onClick={() => {
                              history.push(AdminRoutes.MANAGELIBRARY);
                            }}
                          >
                            Back
                          </ButtonText>
                        </Col>
                      )}
                      <Col>
                        <ActionButtonGroup
                          btnAddText="Add New Object"
                          alertObj={alertObj}
                          isReorder={isReorder}
                          disableReorder={posts.length === 0}
                          setIsReorder={setIsReorder}
                          handleClickAdd={handleClickAdd}
                          handleClickSaveReorder={handleClickSaveReorder}
                          handleClickCancelReorder={handleClickCancelReorder}
                        />
                      </Col>
                    </Row>
                    {isReorder && <ReorderNote />}
                    <Row>
                      <Col>
                        <SortableTable
                          disabled={!isReorder}
                          colHeaders={colHeaders}
                          setDataSource={setPosts}
                          dataSource={posts}
                          tableBody={
                            posts &&
                            posts.map((row, key) => (
                              <Tr key={key}>
                                <Td className={isReorder ? "vss-handle" : ""}>
                                  <PostClassification
                                    name={
                                      row.libraryClassificationName
                                        ? row.libraryClassificationName
                                        : ""
                                    }
                                    classificationId={
                                      row.libraryClassificationId
                                    }
                                    productName={
                                      row.productName ? row.productName : ""
                                    }
                                  ></PostClassification>
                                </Td>
                                <Td className={isReorder ? "vss-handle" : ""}>
                                  <PostTypeIcon type={row.type}></PostTypeIcon>
                                </Td>
                                <Td className={isReorder ? "vss-handle" : ""}>
                                  {row.title}
                                </Td>
                                <Td>
                                  {!isReorder &&
                                    LibraryHelper.hasDynamicUrlPermission(
                                      row,
                                      data.user
                                    ) && (
                                      <ActionMenu
                                        dataId={row.id}
                                        dataName={row.title}
                                        handleClickEdit={handleClickEdit}
                                        handleClickDelete={handleClickDelete}
                                      ></ActionMenu>
                                    )}
                                </Td>
                              </Tr>
                            ))
                          }
                        />
                      </Col>
                    </Row>
                  </Grid>
                )}
              </>
            )}
          </GlobalAlertContext.Consumer>
        )}
      </AuthContext.Consumer>
    );
  }
}
export default ManageCategory;
